import { useCallback, useEffect, useRef, useState } from 'react'

import { easeInOutQuad } from 'helpers/animationFunctions'

const FRAME_DURATION = 1000 / 60

type TAnimationConfig = {
  duration: number
  to?: number
  animationFunction?: (t: number) => number
  callback?: () => void
}

export const useProgressAnimation = ({
  duration,
  to = 100,
  animationFunction = easeInOutQuad,
  callback,
}: TAnimationConfig): {
  counter: number
  isFinished: boolean
  progressAnimationPlayback: (stopAt?: number) => void
} => {
  const [counter, setCounter] = useState(0)
  const frameRef = useRef(1)
  const intervalRef = useRef<ReturnType<typeof setInterval>>()
  const callbackRef = useRef(callback)

  callbackRef.current = callback

  const stopAnimation = useCallback(() => {
    if (intervalRef.current === undefined) return

    clearInterval(intervalRef.current)
    intervalRef.current = undefined
  }, [])

  const progressAnimationPlayback = useCallback(
    (stopAt?: number) => {
      stopAnimation()

      const totalFrames = Math.max(1, Math.round(duration / FRAME_DURATION))

      intervalRef.current = setInterval(() => {
        const elapsedTime = Math.min(frameRef.current / totalFrames, 1)
        const value = Math.round(to * animationFunction(elapsedTime))

        frameRef.current += 1

        if (stopAt !== undefined && value >= stopAt) {
          setCounter(stopAt)
          stopAnimation()
          return
        }

        if (elapsedTime >= 1) {
          setCounter(to)
          stopAnimation()
          return
        }

        setCounter(Math.max(0, Math.min(to, value)))
      }, FRAME_DURATION)
    },
    [animationFunction, duration, stopAnimation, to],
  )

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>

    if (counter >= to && callbackRef.current) {
      timerId = setTimeout(() => callbackRef.current?.(), 0)
    }

    return () => clearTimeout(timerId)
  }, [counter, to])

  useEffect(() => stopAnimation, [stopAnimation])

  return {
    counter,
    isFinished: counter === to,
    progressAnimationPlayback,
  }
}
